import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Nav, Tab } from 'react-bootstrap';
import { API_ENDPOINTS } from '../../config/properties';
import { useLotteryStats } from '../../hooks/useLotteryStats';
import LotteryStatsForm from '../common/LotteryStatsForm';
import LotteryStatsDisplay from '../common/LotteryStatsDisplay';
import SuperEnalottoExtractionHistory from './SuperEnalottoExtractionHistory';
import './superenalotto.css';

/**
 * SuperEnalotto Main Component
 * Displays SuperEnalotto lottery statistics with AI predictions
 * Uses reusable hooks and components for better maintainability
 */
const SuperEnalottoMain = () => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('combinations');

  // Use the lottery stats hook with Super Enalotto specific defaults
  const {
    combinations,
    topNumbers,
    coldNumbers,
    lastCombination,
    loading,
    error,
    additionalData,
    topN,
    minFrequentMatch,
    minPreferredMatch,
    preferredNum,
    setTopN,
    setMinFrequentMatch,
    setMinPreferredMatch,
    handlePreferredNumChange,
    handleSubmit,
    handleReset,
  } = useLotteryStats(API_ENDPOINTS.superEnalottoStats, {
    topN: 6,
    minFrequentMatch: 3,
    minPreferredMatch: 1,
    preferredNum: '7,12',
  });

  return (
    <div className="container mt-5">
      <div className="lottery-header mb-4 super-enalotto-header">
        <h1>{t('titlePageTabSuperEnalotto')}</h1>
        <p className="mb-0 mt-2" style={{ fontSize: '0.95rem', opacity: 0.95 }}>
          {t('superEnalottoDescription')}
        </p>
      </div>

      <div className="card shadow-lg">
        <Tab.Container activeKey={activeTab} onSelect={(k) => setActiveTab(k)}>
          <div className="card-header border-bottom">
            <Nav variant="tabs" className="nav-fill">
              <Nav.Item>
                <Nav.Link eventKey="combinations" className="fw-600">
                  <i className="bi bi-calculator"></i> {t('combinations')}
                </Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="history" className="fw-600">
                  <i className="bi bi-clock-history"></i> {t('extractionHistory')}
                </Nav.Link>
              </Nav.Item>
            </Nav>
          </div>

          <Tab.Content>
            <Tab.Pane eventKey="combinations">
              <div className="card-body p-4">
                {/* Parameters Form */}
                <div className="lottery-filters mb-4">
                  <div className="row g-3">
                    <div className="col-12">
                      <h6 className="card-title fw-600 mb-3">{t('filterParameters')}</h6>
                      <LotteryStatsForm
                        topN={topN}
                        minFrequentMatch={minFrequentMatch}
                        minPreferredMatch={minPreferredMatch}
                        preferredNum={preferredNum}
                        onTopNChange={setTopN}
                        onMinFrequentMatchChange={setMinFrequentMatch}
                        onMinPreferredMatchChange={setMinPreferredMatch}
                        onPreferredNumChange={handlePreferredNumChange}
                        onSubmit={handleSubmit}
                        onReset={handleReset}
                        loading={loading}
                      />
                    </div>
                  </div>
                </div>

                {/* Statistics Display */}
                <LotteryStatsDisplay
                  combinations={combinations}
                  topNumbers={topNumbers}
                  coldNumbers={coldNumbers}
                  lastCombination={lastCombination}
                  loading={loading}
                  error={error}
                  additionalData={additionalData}
                  title={t('superEnalottoStatistics')}
                  showColdNumbers={true}
                />
              </div>
            </Tab.Pane>

            <Tab.Pane eventKey="history">
              <div className="card-body p-4">
                <SuperEnalottoExtractionHistory />
              </div>
            </Tab.Pane>
          </Tab.Content>
        </Tab.Container>
      </div>
    </div>
  );
};

export default SuperEnalottoMain;
